import { RichText } from './rich-text';

const COMMANDS = ['bold', 'italic', 'underline'] as const;

/**
 * A click-to-edit rich text row in the prototype panel: shows the page's text formatted, and edits it with Bold / Italic / Underline
 * until Save. Prototype chrome only.
 */
export class PrototypeRichTextField {
    private readonly preview: HTMLButtonElement;
    private readonly previewText: HTMLElement;
    private readonly empty: HTMLElement | null;
    private readonly editor: HTMLElement;
    private readonly input: HTMLElement;
    private readonly toolbar: HTMLElement | null;
    private readonly reset: HTMLButtonElement | null;
    private readonly onSave: (html: string | null) => void;
    private shown = '';

    constructor(element: HTMLElement, onSave: (html: string | null) => void) {
        const preview = element.querySelector<HTMLButtonElement>('[data-prototype-text-preview]');
        const previewText = preview?.querySelector<HTMLElement>('.text');
        const editor = element.querySelector<HTMLElement>('[data-prototype-text-editor]');
        const input = editor?.querySelector<HTMLElement>('[data-prototype-rich-input]');
        if (!preview || !previewText || !editor || !input) {
            throw new Error('Incomplete prototype rich text field');
        }

        this.preview = preview;
        this.previewText = previewText;
        this.empty = preview.querySelector('.empty');
        this.editor = editor;
        this.input = input;
        this.toolbar = editor.querySelector('[data-prototype-rich-toolbar]');
        this.reset = element.querySelector('[data-prototype-text-reset]');
        this.onSave = onSave;

        preview.addEventListener('click', () => this.open());
        editor.querySelector('[data-prototype-text-save]')?.addEventListener('click', () => this.save());
        editor.querySelector('[data-prototype-text-cancel]')?.addEventListener('click', () => this.close());
        this.reset?.addEventListener('click', () => this.onSave(null));

        // pointerdown, not click: a click would move focus out of the text and lose the selection the command applies to.
        this.toolbar?.addEventListener('pointerdown', (e: PointerEvent) => {
            const button = e.target instanceof Element ? e.target.closest<HTMLButtonElement>('[data-rich-command]') : null;
            if (button) {
                e.preventDefault();
                this.run(button.getAttribute('data-rich-command') ?? '');
            }
        });
        this.toolbar?.addEventListener('keydown', (e: KeyboardEvent) => {
            const button = e.target instanceof Element ? e.target.closest<HTMLButtonElement>('[data-rich-command]') : null;
            if (button && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                this.input.focus();
                this.run(button.getAttribute('data-rich-command') ?? '');
            }
        });

        input.addEventListener('keydown', (e: KeyboardEvent) => {
            // Esc cancels the edit only; nothing further up gets it.
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                this.close();
            } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                this.save();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                document.execCommand('insertLineBreak');
            }
        });
        // A paste keeps bold, italic and underline only, so the box shows what the page will.
        input.addEventListener('paste', (e: ClipboardEvent) => {
            e.preventDefault();
            const html = e.clipboardData?.getData('text/html');
            const text = e.clipboardData?.getData('text/plain') ?? '';
            document.execCommand('insertHTML', false, html ? RichText.sanitize(html) : RichText.sanitize(PrototypeRichTextField.escape(text).replace(/\r?\n/g, '<br>')));
        });
        document.addEventListener('selectionchange', () => this.syncToolbar());
    }

    /** The html the page shows; `typed` when it is not the default, which enables "Default text". */
    public setText(html: string, typed: boolean) {
        if (!this.editor.hidden && html !== this.shown) {
            this.close(false);
        }

        this.shown = html;
        this.previewText.replaceChildren(RichText.fragment(html));
        if (this.empty) {
            this.empty.hidden = !RichText.isEmpty(html);
        }

        if (this.reset) {
            this.reset.disabled = !typed;
        }
    }

    public close(restoreFocus = true) {
        this.editor.hidden = true;
        this.preview.hidden = false;
        if (restoreFocus) {
            this.preview.focus();
        }
    }

    private open() {
        this.input.replaceChildren(RichText.fragment(this.shown));
        this.editor.hidden = false;
        this.preview.hidden = true;
        this.input.focus();
        const range = document.createRange();
        range.selectNodeContents(this.input);
        range.collapse(false);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
        this.syncToolbar();
    }

    private save() {
        const html = RichText.isEmpty(this.input.innerHTML) ? '' : RichText.sanitize(this.input.innerHTML);
        this.close();
        if (html !== this.shown) {
            this.onSave(html);
        }
    }

    private run(command: string) {
        if (command === 'clear') {
            document.execCommand('removeFormat');
        } else if ((COMMANDS as readonly string[]).includes(command)) {
            document.execCommand(command);
        }

        this.syncToolbar();
    }

    /** Each button pressed while the selection is in that style. */
    private syncToolbar() {
        if (this.editor.hidden || !this.toolbar) {
            return;
        }

        for (const command of COMMANDS) {
            this.toolbar.querySelector(`[data-rich-command="${command}"]`)?.setAttribute('aria-pressed', document.queryCommandState(command).toString());
        }
    }

    private static escape(text: string): string {
        const span = document.createElement('span');
        span.textContent = text;
        return span.innerHTML;
    }
}
